import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Clock, AlertCircle, CheckCircle2, ArrowRight, Car, Plane, Building2, ShoppingBag } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const problems = [
  { icon: AlertCircle, text: "You don't know how to phrase it" },
  { icon: Clock,       text: 'Writing takes time you don\'t have' },
  { icon: FileText,    text: 'You miss key details and legal references' },
];

const solutions = [
  { icon: CheckCircle2, text: 'Guided form — you answer questions, AI writes the letter' },
  { icon: CheckCircle2, text: 'Professional tone and correct formalities' },
  { icon: CheckCircle2, text: 'Ready to send — download as PDF' },
];

const useCases = [
  { icon: Car,        label: 'Parking fines',    desc: 'Appeal incorrect parking tickets and penalties.' },
  { icon: Plane,      label: 'Flight delays',    desc: 'Claim compensation under EU Regulation 261/2004.' },
  { icon: Building2,  label: 'Authorities',      desc: 'Appeal decisions from government agencies.' },
  { icon: ShoppingBag, label: 'Companies',       desc: 'Product complaints, warranty claims, and billing disputes.' },
];

const EnKlagobotPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="Klagobot · Complaint letters"
        heading={<>Create complaint letters and appeals<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>in minutes</span></>}
        subtext="Fill in your case and get a ready-to-send document. No legal knowledge required."
        ctaLabel="Try Klagobot"
        ctaHref="/en/contact"
      />

      {/* Trust strip */}
      <div className="bg-white border-b border-border-light py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: '⏱', text: 'Ready letter in 2–5 minutes' },
              { icon: '⚖️', text: 'Based on Swedish consumer law' },
              { icon: '📎', text: 'Used for complaints and appeals' },
              { icon: '📥', text: 'Download as PDF' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <span className="text-base" aria-hidden="true">{icon}</span>
                <span className="text-sm font-medium text-text-secondary">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Problem */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">The problem</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Writing complaints is hard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {problems.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }} className="flex gap-4 items-start bg-red-50 rounded-2xl border border-red-100 p-6">
                <Icon className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">The solution</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Klagobot does the work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {solutions.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }} className="flex gap-4 items-start bg-white rounded-2xl border border-border-light p-6 shadow-sm">
                <Icon className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Use cases</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">What can you complain about?</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {useCases.map(({ icon: Icon, label, desc }) => (
              <motion.div key={label} whileHover={{ y: -4 }} className="bg-white rounded-2xl border border-border-light p-6 text-center shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all duration-300">
                <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 items-center justify-center mb-4 shadow-md">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-[15px] font-bold text-text-primary mb-2">{label}</h3>
                <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-extrabold text-text-primary mb-4">Ready to try?</h2>
          <p className="text-text-secondary mb-8 max-w-md mx-auto">Klagobot is under active development. Register your interest and we'll notify you when it's ready.</p>
          <Link to="/en/contact">
            <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
              Try Klagobot
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </Link>
          <p className="text-xs text-text-muted mt-2">Ready letter in 2–5 minutes · Ready to send immediately</p>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnKlagobotPage;
